import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiUrl } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '../components/ui'

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains

/**
 * LogoutPage - Global logout endpoint for the platform
 *
 * This page handles global logout from the u.software platform.
 * Called when an app requests `logout({ global: true })`.
 *
 * Flow:
 * 1. Call backend /auth/logout to clear the session cookie
 * 2. Redirect to the return_to URL (or show success message)
 */
export function LogoutPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  const returnTo = searchParams.get('return_to')

  useEffect(() => {
    async function performLogout() {
      try {
        // Call backend to clear session cookie
        const response = await fetch(`${getApiUrl()}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include', // Important: include cookies
        })

        if (!response.ok) {
          // 401 means no session - that's fine, we're already logged out
          if (response.status !== 401) {
            throw new Error('Logout failed')
          }
        }

        setStatus('success')

        // Redirect to return_to URL after a short delay
        if (returnTo) {
          setTimeout(() => {
            window.location.href = returnTo
          }, 500)
        }
      } catch (err) {
        console.error('Logout error:', err)
        setError(err instanceof Error ? err.message : 'An error occurred during logout')
        setStatus('error')

        // Even on error, redirect after showing message
        if (returnTo) {
          setTimeout(() => {
            window.location.href = returnTo
          }, 2000)
        }
      }
    }

    performLogout()
  }, [returnTo])

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>
            {status === 'loading' && 'Signing out...'}
            {status === 'success' && 'Signed out'}
            {status === 'error' && 'Sign out error'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-4">
              <div className="mb-4 text-[var(--success)]">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-[var(--text-secondary)]">
                You have been signed out of u.software.
              </p>
              {returnTo && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  Redirecting you back...
                </p>
              )}
              {!returnTo && (
                <div className="mt-6">
                  <a
                    href="/login"
                    className="text-sm font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                  >
                    Sign in again
                  </a>
                </div>
              )}
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-4">
              <div className="mb-4 text-[var(--error)]">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <p className="text-[var(--text-secondary)]">
                {error || 'Failed to sign out. You may already be signed out.'}
              </p>
              {returnTo && (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  Redirecting you back...
                </p>
              )}
              <div className="mt-6">
                <a
                  href="/"
                  className="text-sm font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                >
                  Go to u.software home
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
